import * as THREE from 'three';
import { findPartDef } from './partsCatalog';

const _localMeshBox = new THREE.Box3();
const _tmpMeshBox = new THREE.Box3();
const _tmpInvRoot = new THREE.Matrix4();
const _tmpRel = new THREE.Matrix4();
const _tmpCenter = new THREE.Vector3();
const _tmpSize = new THREE.Vector3();
const _tmpWorldCoreBox = new THREE.Box3();
const CORE_BOUNDS_CACHE_VERSION = 2;

function getLocalMeshBounds(obj: THREE.Object3D): THREE.Box3 | null {
  const cached = (obj.userData as any).__localMeshBoundsForCore as THREE.Box3 | undefined;
  if (cached && !cached.isEmpty()) return cached;

  _localMeshBox.makeEmpty();
  obj.updateMatrix();
  obj.updateMatrixWorld(true);
  _tmpInvRoot.copy(obj.matrixWorld).invert();

  let hasMesh = false;
  obj.traverse((node: THREE.Object3D) => {
    const mesh = node as THREE.Mesh;
    if (!mesh.isMesh) return;

    const geom = mesh.geometry as THREE.BufferGeometry | undefined;
    if (!geom) return;
    if (!geom.boundingBox) geom.computeBoundingBox();
    if (!geom.boundingBox) return;

    _tmpMeshBox.copy(geom.boundingBox);
    _tmpRel.multiplyMatrices(_tmpInvRoot, mesh.matrixWorld);
    _tmpMeshBox.applyMatrix4(_tmpRel);
    if (_tmpMeshBox.isEmpty()) return;
    _localMeshBox.union(_tmpMeshBox);
    hasMesh = true;
  });

  if (!hasMesh || _localMeshBox.isEmpty()) return null;
  const out = _localMeshBox.clone();
  (obj.userData as any).__localMeshBoundsForCore = out;
  return out;
}

function inferBrickLikeCoreFromBounds(localBounds: THREE.Box3): THREE.Box3 | null {
  localBounds.getSize(_tmpSize);
  if (_tmpSize.x <= 1e-4 || _tmpSize.y <= 1e-4 || _tmpSize.z <= 1e-4) return null;

  const studsW = Math.max(1, Math.round(_tmpSize.x / 20));
  const studsD = Math.max(1, Math.round(_tmpSize.z / 20));

  // Only infer for near-rectangular LEGO-like footprints.
  if (Math.abs(_tmpSize.x - studsW * 20) > 8 || Math.abs(_tmpSize.z - studsD * 20) > 8) {
    return null;
  }

  // Use floor, not round, so stud height is never promoted into body height.
  let bodyH = Math.floor((_tmpSize.y + 1e-4) / 8) * 8;
  bodyH = THREE.MathUtils.clamp(bodyH, 8, _tmpSize.y);

  const bottomY = localBounds.min.y;
  const center = localBounds.getCenter(_tmpCenter);
  const halfW = studsW * 10;
  const halfD = studsD * 10;

  return new THREE.Box3(
    new THREE.Vector3(center.x - halfW, bottomY, center.z - halfD),
    new THREE.Vector3(center.x + halfW, bottomY + bodyH, center.z + halfD)
  );
}

export function getLocalCoreBoxForObject(obj: THREE.Object3D): THREE.Box3 | null {
  const cached = (obj.userData as any).__localCoreCollisionBox as THREE.Box3 | undefined;
  const cachedVersion = Number((obj.userData as any).__localCoreCollisionBoxVersion ?? 0);
  if (cached && !cached.isEmpty() && cachedVersion === CORE_BOUNDS_CACHE_VERSION) return cached;

  const partFile = String(obj.userData?.partFile ?? '');
  const def = findPartDef(partFile);
  const meshLocal = getLocalMeshBounds(obj);
  let out: THREE.Box3 | null = null;

  if (def) {
    const w = def.studs.w * 20;
    const d = def.studs.d * 20;
    const h = def.studs.plates * 8;

    if (meshLocal && !meshLocal.isEmpty()) {
      const center = meshLocal.getCenter(_tmpCenter);
      const minY = meshLocal.min.y;
      out = new THREE.Box3(
        new THREE.Vector3(center.x - w / 2, minY, center.z - d / 2),
        new THREE.Vector3(center.x + w / 2, minY + h, center.z + d / 2)
      );
    } else {
      out = new THREE.Box3(
        new THREE.Vector3(-w / 2, 0, -d / 2),
        new THREE.Vector3(w / 2, h, d / 2)
      );
    }
  } else if (meshLocal && !meshLocal.isEmpty()) {
    out = inferBrickLikeCoreFromBounds(meshLocal) ?? meshLocal.clone();
  }

  if (!out || out.isEmpty()) return null;
  (obj.userData as any).__localCoreCollisionBox = out;
  (obj.userData as any).__localCoreCollisionBoxVersion = CORE_BOUNDS_CACHE_VERSION;
  return out;
}

export function getWorldCoreBoxForObject(obj: THREE.Object3D, out: THREE.Box3): boolean {
  const local = getLocalCoreBoxForObject(obj);
  if (!local) return false;

  obj.updateMatrix();
  obj.updateMatrixWorld(true);
  out.copy(local).applyMatrix4(obj.matrixWorld);
  return !out.isEmpty();
}

export function sampleCoreTopYAtXZ(x: number, z: number, objects: THREE.Object3D[]): number {
  let supportY = 0;

  for (const obj of objects) {
    if (!obj.visible) continue;
    if (!getWorldCoreBoxForObject(obj, _tmpWorldCoreBox)) continue;
    if (x < _tmpWorldCoreBox.min.x || x > _tmpWorldCoreBox.max.x) continue;
    if (z < _tmpWorldCoreBox.min.z || z > _tmpWorldCoreBox.max.z) continue;
    supportY = Math.max(supportY, _tmpWorldCoreBox.max.y);
  }

  return supportY;
}
